import React, { useEffect, useState } from 'react'
import type { InterviewQuestionOptionsResource } from '@entities/api/Interview'
import dayjs from '@utils/dayjs'
import { useFormContext } from 'react-hook-form'
import HistoryDate from './HistoryDate'

interface Props {
  prefix: string
  itemName: string | null
  required: boolean
  options?: InterviewQuestionOptionsResource | null
}

interface HistoryDateParams {
  date: dayjs.Dayjs
  minDate?: dayjs.Dayjs
  maxDate: dayjs.Dayjs
  legend: string
  unknown: boolean
}

const History: React.FC<Props> = ({ prefix, itemName, required, options }) => {
  const { register, watch, setValue } = useFormContext()
  const legend = options?.legend || []
  const includeToday = options?.include_today
  const countName = `${prefix}.history_value.count`

  const createHistory = () => {
    const h: HistoryDateParams[] = []
    const count = watch(countName)
    let maxDate = dayjs.tz(dayjs().tz().format('YYYY-MM-DD'))
    if (!includeToday) {
      maxDate = maxDate.add(-1, 'days')
    }
    for (let i = 0; i < count; i += 1) {
      const dateStr = watch(`${prefix}.history_value.date.${i}`)
      const unknown = watch(`${prefix}.history_value.unknown.${i}`)
      const max = maxDate.add(-count + i + 1, 'days')
      let date: dayjs.Dayjs | null = null
      if (i < count - (includeToday ? 1 : 0) && dateStr) {
        date = dayjs.tz(dateStr)
      }
      if (!date || date.isAfter(max)) {
        date = max
      }
      h.push({
        date,
        maxDate: max,
        legend: legend[i],
        unknown,
      })
    }
    for (let i = 0; i < count; i += 1) {
      if (i > 0) {
        for (let j = i - 1; j >= 0; j -= 1) {
          if (!h[j].unknown && h[j].date) {
            h[i].minDate = h[j].date.add(i - j, 'days')
            break
          }
        }
      }
      if (i < count - 1) {
        for (let j = i + 1; j < count; j += 1) {
          if (!h[j].unknown && h[j].date) {
            h[i].maxDate = h[j].date.add(i - j, 'days')
            break
          }
        }
      }
    }
    h.forEach((item) => {
      if (item.date.isAfter(item.maxDate)) {
        item.date = item.maxDate
      }
      if (item.minDate && item.date.isBefore(item.minDate)) {
        item.date = item.minDate
      }
    })
    let latest
    if (includeToday) {
      latest = h.pop()
    }
    return {
      history: h,
      latest,
    }
  }

  const count = watch(countName)
  const [history, setHistory] = useState(() => createHistory())

  useEffect(() => {
    if (!history.latest) {
      return
    }
    setValue(
      `${prefix}.history_value.date.${history.history.length}`,
      history.latest.date.format('YYYY-MM-DD')
    )
  }, [history])

  const errorPrefix = `${itemName ? `${itemName}について、` : ''}`
  const choices: { id: number; body: string }[] = []
  legend.forEach((body, index) => {
    if (body) {
      choices.push({ id: index + 1, body })
    }
  })

  const handleChangeHistory = () => {
    setHistory(createHistory())
  }

  const handleChangeRadio = (event) => {
    setValue(countName, event.target.value)
    handleChangeHistory()
  }

  return (
    <>
      <div className="row" data-toggle="buttons">
        <input
          ref={register({
            valueAsNumber: true,
            required: required && `${errorPrefix}回数を選択してください`,
          })}
          type="hidden"
          name={countName}
        />
        {choices.map((choice, index) => {
          const id = `${prefix}_count_${choice.id}`
          const selected = choice.id === count
          return (
            <div
              key={id}
              className={`col-sm-6 ${index % 2 === 0 ? 'pr-sm-2' : 'pl-sm-2'} mb-3 form-group btn-group-toggle`}
            >
              <label
                id={id}
                className={`btn btn-light btn-block h-100 form-control ${selected ? 'active' : ''}`}
              >
                <input
                  type="radio"
                  id={id}
                  value={choice.id}
                  defaultChecked={selected}
                  className="btn-check"
                  onClick={handleChangeRadio}
                />
                <div>{choice.body}</div>
              </label>
            </div>
          )
        })}
      </div>
      {history.history.map((h, index) => {
        const id = `${prefix}_history_${index}`
        return (
          <div key={id}>
            <div className="row pt-4 pb-3">
              <div className="col">
                <span className="badge badge-primary align-text-bottom font-weight-normal mr-2">
                  必須
                </span>
                <span className="font-weight-bold">
                  {index + 1}
                  回目の接種日はいつですか
                </span>
              </div>
            </div>
            <HistoryDate
              prefix={prefix}
              itemName={h.legend}
              index={index}
              minDate={h.minDate}
              maxDate={h.maxDate}
              onChange={handleChangeHistory}
            />
          </div>
        )
      })}
      {history.latest && (
        <input
          ref={register()}
          type="hidden"
          name={`${prefix}.history_value.date.${history.history.length}`}
        />
      )}
    </>
  )
}

export default History
